import { Sun, Moon, Cloud, CloudSun, CloudMoon, CloudRain, CloudDrizzle } from 'lucide-react';
import { WeatherCondition, TimeOfDay } from '../types';

interface WeatherIconProps {
  condition: WeatherCondition;
  timeOfDay: TimeOfDay;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

/**
 * Maps size prop to actual pixel dimensions
 */
const sizeMap = {
  sm: 'h-8 w-8',
  md: 'h-12 w-12',
  lg: 'h-16 w-16',
  xl: 'h-24 w-24',
};

/**
 * WeatherIcon component that renders the appropriate weather icon
 * based on the condition and time of day
 */
export function WeatherIcon({
  condition,
  timeOfDay,
  size = 'md',
  className = '',
}: WeatherIconProps) {
  const sizeClass = sizeMap[size];
  const baseClass = `${sizeClass} ${className}`;

  // Determine icon based on condition and time of day
  const getIcon = () => {
    const isNight = timeOfDay === 'night';

    switch (condition) {
      case 'clear':
        return isNight ? (
          <Moon className={`${baseClass} text-gray-400 dark:text-gray-500`} />
        ) : (
          <Sun className={`${baseClass} text-gray-700 dark:text-gray-400`} />
        );

      case 'mostly_clear':
        return isNight ? (
          <CloudMoon className={`${baseClass} text-gray-400 dark:text-gray-500`} />
        ) : (
          <CloudSun className={`${baseClass} text-gray-700 dark:text-gray-400`} />
        );

      case 'partly_cloudy':
        return isNight ? (
          <CloudMoon className={`${baseClass} text-gray-500 dark:text-gray-500`} />
        ) : (
          <CloudSun className={`${baseClass} text-gray-600 dark:text-gray-400`} />
        );

      case 'mostly_cloudy':
        return <Cloud className={`${baseClass} text-gray-600 dark:text-gray-400`} />;

      case 'cloudy':
        return <Cloud className={`${baseClass} text-gray-700 dark:text-gray-500`} />;

      case 'light_rain':
        return <CloudDrizzle className={`${baseClass} text-gray-600 dark:text-gray-500`} />;

      case 'rain':
        return <CloudRain className={`${baseClass} text-gray-700 dark:text-gray-600`} />;

      case 'heavy_rain':
        return <CloudRain className={`${baseClass} text-gray-800 dark:text-gray-700`} />;

      default:
        return <Cloud className={`${baseClass} text-gray-600`} />;
    }
  };

  return <div className="inline-flex items-center justify-center">{getIcon()}</div>;
}

/**
 * Simplified WeatherIcon that doesn't require timeOfDay
 * Uses sensible defaults based on condition
 */
export function SimpleWeatherIcon({
  condition,
  size = 'md',
  className = '',
}: {
  condition: WeatherCondition;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}) {
  // Default to day for clearer icons
  return <WeatherIcon condition={condition} timeOfDay="day" size={size} className={className} />;
}
